import './styles/GenericModal.css';

import { As, Icon } from '@chakra-ui/react';
import { IconX } from '@tabler/icons';
import { ButtonComponent } from 'components/buttons/ButtonComponent';
import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

interface IGenericModalProps {
	onCancel?: () => void;
	onSave?: () => void;
	onClose: () => void;
	isOpen: boolean;
	isSaving?: boolean;
	title?: string;
	titleIcon?: As | ReactNode;
	closeIcon?: As | ReactNode;
	children?: ReactNode;
	cancelButtonText?: string;
	saveButtonText?: string;
	showFooter?: boolean;
	showCancelButton?: boolean;
	showSaveButton?: boolean;
	closeOnOverlayClick?: boolean;
	closeOnEsc?: boolean;
	className?: string;
	overlayClassName?: string;
	headerClassName?: string;
	bodyClassName?: string;
	footerClassName?: string;
	disableSaveButton?: boolean;
	disableCancelButton?: boolean;
	maxWidth?: string;
	maxHeight?: string;
	customHeader?: ReactNode;
	customFooter?: ReactNode;
	customCancelButton?: ReactNode;
	customSaveButton?: ReactNode;
	closeIconBoxSize?: number;
	titleIconBoxSize?: number;
	titleIconColor?: string;
	closeIconColor?: string;
	zIndex?: number;
}

const GenericModal = ({
	onClose,
	onCancel,
	onSave,
	isOpen,
	isSaving = false,
	title,
	titleIcon,
	closeIcon,
	children,
	cancelButtonText = 'Cancelar',
	saveButtonText = 'Salvar',
	showFooter = true,
	showCancelButton = true,
	showSaveButton = true,
	closeOnOverlayClick = true,
	closeOnEsc = true,
	className = '',
	overlayClassName = '',
	headerClassName = '',
	bodyClassName = '',
	footerClassName = '',
	disableCancelButton = false,
	disableSaveButton = false,
	maxWidth = '600px',
	maxHeight = '90vh',
	customHeader,
	customFooter,
	customCancelButton,
	customSaveButton,
	closeIconBoxSize = 5,
	titleIconBoxSize = 12,
	titleIconColor = 'easyRED.300',
	closeIconColor = 'easyBLUE.400',
	zIndex = 1000,
}: IGenericModalProps): JSX.Element | null => {
	const [isShaking, setIsShaking] = useState(false);

	useEffect(() => {
		if (!isOpen || !closeOnEsc) return;

		const handleEsc = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				onClose();
			}
		};

		document.addEventListener('keydown', handleEsc);
		return () => document.removeEventListener('keydown', handleEsc);
	}, [isOpen, onClose, closeOnEsc]);

	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = 'unset';
		}

		return () => {
			document.body.style.overflow = 'unset';
		};
	}, [isOpen]);

	const handleOverlayClick = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			if (e.target === e.currentTarget) {
				if (closeOnOverlayClick) {
					onClose();
				} else {
					setIsShaking(true);
					setTimeout(() => setIsShaking(false), 600);
				}
			}
		},
		[closeOnOverlayClick, onClose]
	);

	const handleCancel = useCallback(() => {
		if (onCancel) {
			onCancel();
		} else {
			onClose();
		}
	}, [onCancel, onClose]);

	if (!isOpen) return null;

	const renderIcon = (icon: As | ReactNode, size: number, color: string) => {
		if (React.isValidElement(icon)) {
			return icon;
		}

		if (
			typeof icon === 'function' ||
			(typeof icon === 'object' && icon !== null)
		) {
			return (
				<Icon
					as={icon as As}
					boxSize={size}
					color={color}
				/>
			);
		}

		return null;
	};

	const defaultHeader = (
		<div className={`generic-modal-header ${headerClassName}`}>
			<div className='generic-modal-header-content'>
				{titleIcon &&
					renderIcon(titleIcon, titleIconBoxSize, titleIconColor)}
				{title && (
					<h2
						id='modal-title'
						className='generic-modal-title'
					>
						{title}
					</h2>
				)}
			</div>
			<button
				onClick={onClose}
				className='generic-modal-close-button'
				aria-label='Fechar modal'
			>
				{closeIcon ? (
					renderIcon(closeIcon, closeIconBoxSize, closeIconColor)
				) : (
					<Icon
						as={IconX}
						boxSize={closeIconBoxSize}
						color={closeIconColor}
					/>
				)}
			</button>
		</div>
	);

	const defaultCancelButton = (
		<ButtonComponent
			data-test='cancel-button-modal'
			title={cancelButtonText}
			onClick={handleCancel}
			disabled={disableCancelButton || isSaving}
		/>
	);

	const defaultSaveButton = (
		<ButtonComponent
			data-test='save-button-modal'
			title={isSaving ? 'Salvando...' : saveButtonText}
			type='primary'
			onClick={onSave}
			disabled={disableSaveButton || isSaving}
		/>
	);

	const defaultFooter = (
		<div className={`generic-modal-footer ${footerClassName}`}>
			{showCancelButton && (customCancelButton || defaultCancelButton)}
			{showSaveButton && (customSaveButton || defaultSaveButton)}
		</div>
	);

	const modalContent = (
		<div
			className={`generic-modal-overlay ${overlayClassName}`}
			onClick={handleOverlayClick}
			style={{ zIndex }}
		>
			<div
				className={`generic-modal-container ${className} ${
					isShaking ? 'generic-modal-shake' : ''
				}`}
				style={{ maxWidth, maxHeight }}
				role='dialog'
				aria-modal='true'
				aria-labelledby={title ? 'modal-title' : undefined}
			>
				{customHeader ||
					((title || titleIcon || closeIcon !== false) &&
						defaultHeader)}

				{children && (
					<div className={`generic-modal-body ${bodyClassName}`}>
						{children}
					</div>
				)}

				{showFooter &&
					(customFooter ||
						((showCancelButton || showSaveButton) &&
							defaultFooter))}
			</div>
		</div>
	);

	return ReactDOM.createPortal(modalContent, document.body);
};

export default GenericModal;
